import { Action, createReducer, on } from '@ngrx/store';
import { LoadStatus } from '../../load-status';
import {
    initRecipients,
    recipientsLoadedFromStorage,
    recipientsSaveError,
    removeRecipientsRequested,
    upsertRecipientsRequested,
} from '../actions/recipients.actions';
import {
    createDefaultSavedRecipients,
    initialRecipientsCollectionState,
    RecipientEntryState,
    RecipientsCollectionState,
    SavedRecipientsState,
} from '../recipients-collection.state';
import { NotificationStateUtilities } from '../../utilities/notification-state-utlities';
import { NotificationIdState } from '../../utilities/notification-id.type';

/**
 * Marks the slice as loading while the hydrate effect reads persisted storage.
 *
 * @param state the recipients slice
 * @returns the slice with the loading status set
 */
export function setRecipientLoadingReducer(
    state: Readonly<RecipientsCollectionState>,
): Readonly<RecipientsCollectionState> {
    const loadStatus = state.loadStatus === LoadStatus.Loaded ? LoadStatus.Loaded : LoadStatus.Loading;
    return { ...state, loadStatus };
}

/**
 * Replaces the full entry collection with the persisted data. Runs once per `initRecipients`
 * dispatch — unlike the legacy `init()`, which pushes onto the in-memory array without
 * clearing it first (so a second call would duplicate every entry), this always replaces.
 *
 * @param state the recipients slice
 * @param props the action payload
 * @param props.entries the persisted collection
 * @returns the slice with the new collection, marked loaded
 */
export function setRecipientEntriesReducer(
    state: Readonly<RecipientsCollectionState>,
    props: { entries: SavedRecipientsState[] },
): Readonly<RecipientsCollectionState> {
    return { ...state, entries: props.entries, loadStatus: LoadStatus.Loaded, error: null };
}

/**
 * Creates the saved-recipients entry for `id` if absent (via `createDefaultSavedRecipients`),
 * then replaces that entry's recipients — the reducer-side equivalent of the legacy
 * `findIndex` / `if (i !== -1) … else push`. Idempotent: dispatching the same payload twice
 * yields the same resulting entry.
 *
 * @param state the recipients slice
 * @param props the action payload
 * @param props.id the storage id whose recipients are being saved
 * @param props.recipients the recipients to store for `id`
 * @param props.notificationId
 * @returns the slice with the entry created or replaced
 */
export function upsertRecipientsReducer(
    state: Readonly<RecipientsCollectionState>,
    props: { notificationId: NotificationIdState; recipients: RecipientEntryState[] },
): Readonly<RecipientsCollectionState> {
    const exists = state.entries.some((entry) =>
        NotificationStateUtilities.compare(entry.notificationId, props.notificationId),
    );
    const base = exists ? state.entries : [...state.entries, createDefaultSavedRecipients(props.notificationId)];
    return {
        ...state,
        entries: base.map((entry) =>
            NotificationStateUtilities.compare(entry.notificationId, props.notificationId)
                ? { ...entry, recipients: props.recipients }
                : entry,
        ),
    };
}

/**
 * Removes the saved-recipients entry for `id`. An unknown id returns the same state
 * reference, mirroring the legacy `index === -1` no-op.
 *
 * @param state the recipients slice
 * @param props the action payload
 * @param props.id the storage id to remove
 * @param props.notificationId
 * @returns the slice without the entry, or the same reference when `id` is unknown
 */
export function removeRecipientsReducer(
    state: Readonly<RecipientsCollectionState>,
    props: { notificationId: NotificationIdState },
): Readonly<RecipientsCollectionState> {
    if (
        !state.entries.some((entry) => NotificationStateUtilities.compare(entry.notificationId, props.notificationId))
    ) {
        return state;
    }
    return {
        ...state,
        entries: state.entries.filter(
            (entry) => !NotificationStateUtilities.compare(entry.notificationId, props.notificationId),
        ),
    };
}

/**
 * Records a persistence failure, keeping the slice's current entries in place
 * (store-first, as the address slice does).
 *
 * @param state the recipients slice
 * @param props the action payload
 * @param props.error the persistence error message
 * @returns the slice with the error recorded
 */
export function setSaveErrorReducer(
    state: Readonly<RecipientsCollectionState>,
    props: { error: string },
): Readonly<RecipientsCollectionState> {
    return { ...state, error: props.error };
}

const createRecipientsReducer = createReducer(
    initialRecipientsCollectionState,
    on(initRecipients, setRecipientLoadingReducer),
    on(recipientsLoadedFromStorage, setRecipientEntriesReducer),
    on(upsertRecipientsRequested, upsertRecipientsReducer),
    on(removeRecipientsRequested, removeRecipientsReducer),
    on(recipientsSaveError, setSaveErrorReducer),
);

export function recipientsReducer(
    state: Readonly<RecipientsCollectionState> | undefined,
    action: Action,
): Readonly<RecipientsCollectionState> {
    return createRecipientsReducer(state, action);
}
